import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sprout } from "lucide-react";

export default function TreeCreate() {
  const navigate = useNavigate();
  const [ref, setRef] = useState(null);
  const [form, setForm] = useState({ name: "", tree_type: "", planting_date: new Date().toISOString().slice(0, 10), pot_size: "", soil_type: "", stature: "Standard", climate_zone: "Temperate", units: "metric", latitude: "", longitude: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [zoneAuto, setZoneAuto] = useState(false);

  useEffect(() => { api.get("/reference").then((r) => setRef(r.data)); }, []);

  // Default the unit system from the browser locale (US/Liberia/Myanmar → Imperial).
  useEffect(() => {
    try {
      const lang = navigator.language || "en";
      let region = "";
      try { region = new Intl.Locale(lang).region || ""; } catch { region = ""; }
      if (!region) region = (lang.split("-")[1] || "").toUpperCase();
      const units = ["US", "LR", "MM"].includes(region) ? "imperial" : "metric";
      setForm((f) => ({ ...f, units }));
    } catch { /* keep metric default */ }
  }, []);

  // Browser geolocation → default climate zone + coordinates (user can override). Non-blocking.
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude, lon = pos.coords.longitude;
        const zone = Math.abs(lat) < 30 ? "Hot/Arid" : Math.abs(lat) > 50 ? "Cool/Humid" : "Temperate";
        setForm((f) => ({ ...f, climate_zone: zone, latitude: lat.toFixed(4), longitude: lon.toFixed(4) }));
        setZoneAuto(true);
      },
      () => {},
      { timeout: 6000 }
    );
  }, []);

  const set = (k, v) => { setForm({ ...form, [k]: v }); if (k === "climate_zone") setZoneAuto(false); };
  const ready = form.name && form.tree_type && form.pot_size && form.soil_type && form.stature;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const payload = {
        ...form,
        latitude: form.latitude === "" ? null : Number(form.latitude),
        longitude: form.longitude === "" ? null : Number(form.longitude),
      };
      const { data } = await api.post("/trees", payload);
      toast.success("Tree profile created · Day 0 set");
      navigate(`/trees/${data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const Field = ({ label, k }) => (
    <div>
      {label && <Label className="text-xs uppercase tracking-[0.2em] text-stone-500">{label}</Label>}
      <Select value={form[k]} onValueChange={(v) => set(k, v)}>
        <SelectTrigger data-testid={`select-${k}`} className="mt-1.5 rounded-xl h-11"><SelectValue placeholder={`Select ${(label || k).toLowerCase()}`} /></SelectTrigger>
        <SelectContent>
          {(ref?.[k === "tree_type" ? "tree_types" : k === "pot_size" ? "pot_sizes" : k === "soil_type" ? "soil_types" : k === "climate_zone" ? "climate_zones" : "statures"] || []).map((o) => (
            <SelectItem key={o} value={o} data-testid={`option-${k}-${o}`}>{o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <Sprout className="w-9 h-9 text-emerald-700" />
          <h1 className="mt-4 font-serif text-4xl text-emerald-950">New Plant / Crop Profile</h1>
          <p className="mt-2 text-stone-500">Five inputs set Day 0 and generate your full milestone care calendar.</p>

          <form onSubmit={submit} className="mt-8 rounded-3xl border border-black/5 bg-white p-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)] space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-stone-500">Plant / Crop nickname</Label>
              <Input data-testid="input-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Backyard Mango" className="mt-1.5 rounded-xl h-11" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Plant / Crop Type" k="tree_type" />
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-stone-500">Planting Date (Day 0)</Label>
                <Input data-testid="input-planting-date" type="date" value={form.planting_date} onChange={(e) => set("planting_date", e.target.value)} className="mt-1.5 rounded-xl h-11" />
              </div>
              <Field label="Pot / Root Size" k="pot_size" />
              <Field label="Soil Type" k="soil_type" />
            </div>
            <Field label="Tree Stature" k="stature" />
            <p className="-mt-2 text-xs text-stone-400">Rootstock scale — sets water volume and minimum pot size. Standard = full size · Semi-Dwarf ≈ 65% · Dwarf ≈ 40%.</p>
            <div>
              <div className="flex items-center gap-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-stone-500">Climate Zone</Label>
                {zoneAuto && <span data-testid="climate-auto-badge" className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700">Auto-detected · edit anytime</span>}
              </div>
              <Field k="climate_zone" />
              <p className="mt-1.5 text-xs text-stone-400">Tunes watering frequency. Hot/Arid waters ~40% more often · Cool/Humid ~30% less.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-stone-500">Location (for rainfall skip)</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-3">
                <Input data-testid="input-latitude" value={form.latitude} onChange={(e) => set("latitude", e.target.value)} placeholder="Latitude" className="rounded-xl h-11" />
                <Input data-testid="input-longitude" value={form.longitude} onChange={(e) => set("longitude", e.target.value)} placeholder="Longitude" className="rounded-xl h-11" />
              </div>
              <p className="mt-1.5 text-xs text-stone-400">Auto-filled from your device if allowed — edit to match your grow site. Enables "skip the soak, rain's coming" alerts.</p>
            </div>
            {error && <p data-testid="tree-error" className="text-sm text-red-600">{error}</p>}
            <Button data-testid="tree-submit" type="submit" disabled={!ready || busy} className="w-full rounded-full h-12 bg-emerald-900 hover:bg-emerald-800 text-white">
              {busy ? "Generating calendar…" : "Create & generate calendar"}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
